import { ArrowDownward } from "@mui/icons-material";
import { Button, CircularProgress, Typography } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import type { ReactNode } from "react";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(226, 114, 91, 0.55); }
  70% { box-shadow: 0 0 0 14px rgba(226, 114, 91, 0); }
  100% { box-shadow: 0 0 0 0 rgba(226, 114, 91, 0); }
`;

interface TermsActionsProps {
  atEnd: boolean;
  idle: boolean;
  accepting: boolean;
  acceptLabel: string;
  onAccept: () => void;
  onJump: () => void;
  /** Ação de recusa, se houver (ex.: "Recusar"): nunca depende de ler o termo. */
  secondary?: ReactNode;
}

/**
 * Dica + botão do aceite de termos. Um botão só, sempre ativo: leva ao fim do
 * termo ("Ir para o fim do termo") e, ao chegar lá, vira o aceite. Parado por
 * 8 s, o botão pulsa e a dica fica mais forte.
 */
export default function TermsActions({
  atEnd,
  idle,
  accepting,
  acceptLabel,
  onAccept,
  onJump,
  secondary,
}: TermsActionsProps) {
  const nudging = !atEnd && idle;

  return (
    <>
      {!atEnd && (
        <Typography
          aria-live="polite"
          sx={{
            color: nudging ? "#f5b041" : "rgba(255,255,255,0.65)",
            fontSize: nudging ? "14px" : "13px",
            fontWeight: nudging ? 600 : 400,
            textAlign: "center",
          }}
        >
          {nudging
            ? "Está com dificuldade? Toque no botão abaixo."
            : "Role o texto ou toque no botão para ir direto ao fim."}
        </Typography>
      )}

      <div className="flex flex-col-reverse sm:flex-row gap-4 pt-2">
        {secondary}
        <Button
          onClick={atEnd ? onAccept : onJump}
          variant="contained"
          fullWidth
          disabled={accepting}
          endIcon={atEnd ? null : <ArrowDownward />}
          sx={{
            background: "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)",
            borderRadius: "20px",
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 700,
            padding: "14px 24px",
            color: "#ffffff",
            border: "2px solid rgba(226, 114, 91, 0.3)",
            boxShadow: "0 8px 24px rgba(226, 114, 91, 0.4)",
            "&:hover": {
              background: "linear-gradient(135deg, #CC3B2A 0%, #E2725B 100%)",
            },
            // Só desabilita durante o envio (spinner sobre fundo apagado).
            "&:disabled": {
              background: "rgba(255,255,255,0.08)",
              color: "rgba(255,255,255,0.3)",
              border: "2px solid rgba(255,255,255,0.05)",
              boxShadow: "none",
            },
            ...(nudging && {
              animation: `${pulse} 1.5s ease-out infinite`,
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }),
          }}
        >
          {accepting ? (
            <CircularProgress size={24} sx={{ color: "#ffffff" }} />
          ) : atEnd ? (
            acceptLabel
          ) : (
            "Ir para o fim do termo"
          )}
        </Button>
      </div>
    </>
  );
}
